/* eslint-disable react/display-name */
/* eslint-disable @typescript-eslint/no-unused-vars */
import rowStyles from '@/components/StoreProductsTable/TableRow.module.css';
import { Modifier, ModifierActiveStatus } from '@/types/menus.types';
import { Box, Grid } from '@mui/material';
import { CellContext } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FZMenuModifierTable from './FZMenuModifierTable';
import styles from './FZStoreModifierItemTable.module.css';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { FZStoreModifierItemTableProps } from '@/components/ModifiersTable/ModifiersTable.types';
import {
  formatModifierPriceCell,
  getModifierActiveStatusForStoreGroup,
  isModifierCategoryAppetizerTopping,
  isModifierCategoryIngredientsPresence,
} from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/modifierDisplay.helpers';

const getCellModifierDescription = (useDevelopTableLayout: boolean) => {
  const CellModifierDescription = ({ row }: CellContext<Modifier, unknown>) => {
    const { modifierName, modifierDescription, namedPriceName } = row.original;
    const showDescription = useDevelopTableLayout || Boolean(modifierDescription);
    const displayName = modifierName || namedPriceName;

    return (
      <Grid container direction="column" justifyContent="center" alignItems="flex-start" width="100%" minWidth="0">
        <Grid item className={useDevelopTableLayout ? styles.developDescription : styles.description}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>{displayName}</div>
        </Grid>
        {showDescription ? (
          <Grid item className={styles.subDescription}>
            <span className={styles.productDescription}>{modifierDescription}</span>
          </Grid>
        ) : null}
      </Grid>
    );
  };

  return CellModifierDescription;
};

function getFirstEightChars(header: string): string {
  return header.substring(0, 8);
}

const PresenceStatusCell = ({ status, onInfoClick }: { status: ModifierActiveStatus; onInfoClick?: () => void }) => {
  if (status === 'ALL') {
    return (
      <span className={styles.presenceCell} data-testid="modifier-presence-all" aria-label="All">
        <span className={styles.presenceFilledCircle} aria-hidden />
      </span>
    );
  }

  if (status === 'SOME') {
    return (
      <span className={styles.presenceCell} data-testid="modifier-presence-some" aria-label="Some">
        <span className={styles.presenceUnfilledCircle} aria-hidden />
        <InfoOutlined
          className={styles.presenceInfoIcon}
          data-testid="modifier-presence-some-info"
          sx={{
            width: '22px',
            height: '22px',
            color: '#266DCC',
            cursor: 'pointer',
          }}
          onClick={onInfoClick}
        />
      </span>
    );
  }

  return (
    <span className={styles.presenceCell} data-testid="modifier-presence-none" aria-label="None">
      <span className={styles.presenceDash} aria-hidden>
        –
      </span>
    </span>
  );
};

const getPresenceDescription = (
  header: string,
  modifierCategory: string,
  setPriceDeviationsData: FZStoreModifierItemTableProps['setPriceDeviationsData'],
) => {
  return ({ row }: CellContext<Modifier, unknown>) => {
    const { modifierId, slotId, modifierWeightId, variantId } = row.original;
    const status = getModifierActiveStatusForStoreGroup(row.original, header);
    return (
      <PresenceStatusCell
        status={status}
        onInfoClick={() =>
          setPriceDeviationsData({
            isPriceDeviationsDrawerOpen: true,
            selectedGroupName: header,
            modifierCategory,
            modifierKeys: [
              {
                modifierId,
                slotId,
                modifierWeightId,
                variantId: variantId as string,
              },
            ],
            cameFromEditDrawer: false,
          })
        }
      />
    );
  };
};

const getPriceDescription = (
  header: string,
  inactiveLabel: string,
  modifierCategory: string,
  setPriceDeviationsData: FZStoreModifierItemTableProps['setPriceDeviationsData'],
) => {
  return ({ row }: CellContext<Modifier, unknown>) => {
    const { storeGroups, modifierId, slotId, modifierWeightId, variantId } = row.original;
    const storeGroup = storeGroups.find((sg) => sg.storeGroupName === header);
    const { text, showRangeInfo } = formatModifierPriceCell(storeGroup?.chosenPriceRange, inactiveLabel);

    return showRangeInfo ? (
      <div className={styles.rangePriceCell}>
        <div className={styles.noWrapPrice}>{text}</div>
        <InfoOutlined
          className={styles.priceRangeInfo}
          sx={{
            width: '22px',
            height: '22px',
            color: '#266DCC',
            cursor: 'pointer',
          }}
          onClick={() =>
            setPriceDeviationsData({
              isPriceDeviationsDrawerOpen: true,
              selectedGroupName: header,
              modifierCategory,
              modifierKeys: [
                {
                  modifierId,
                  slotId,
                  modifierWeightId,
                  variantId: variantId as string,
                },
              ],
              cameFromEditDrawer: false,
            })
          }
        />
      </div>
    ) : (
      <span className={styles.noWrapPrice}>{text}</span>
    );
  };
};

const FZStoreModifierItemTable = (props: FZStoreModifierItemTableProps) => {
  const {
    changeHeaderSize,
    menuModifiers,
    dynamicHeaders,
    selectedRows,
    onRowSelectionChange,
    isLoading,
    storesAndGroups,
    isFranchiseeEditAndFreeze,
    setPriceDeviationsData,
    modifierCategory,
  } = props;

  const { t } = useTranslation();
  const isIngredientsPresence = isModifierCategoryIngredientsPresence(modifierCategory);
  const useDevelopTableLayout = isModifierCategoryAppetizerTopping(modifierCategory);

  const getStoreGroupsCount = useCallback(
    (header: string) => {
      let total = 0;
      let count = 0;
      if (storesAndGroups.origin === 'tab-store') {
        total = storesAndGroups.countByStoreGroup?.find((sg) => sg.storeGroup === header)?.count as number;
        count = storesAndGroups?.stores?.filter((store) => store.storeGroup === header).length as number;
      }

      if (storesAndGroups.origin === 'tab-stores-groups') {
        total = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.storeCount as number;
        count = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.stores.length as number;
      }

      const headerWithFistEightChars = getFirstEightChars(header);

      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
          <strong>{headerWithFistEightChars}</strong>
          <span>
            {count} / {total}
          </span>
        </div>
      );
    },
    [storesAndGroups],
  );

  const columns = useMemo(() => {
    const inactiveLabel = t('common.inactive');
    const lColumns = [
      {
        header: t('menuDetails.storeItem.table.modifier'),
        accessorKey: 'modifierDescription',
        enableSorting: true,
        cell: getCellModifierDescription(useDevelopTableLayout),
        sortingFn: 'sortModifier',
        columnClassname: 'productHeaderStyle',
      } as any,
    ];
    dynamicHeaders.forEach((header, index) => {
      lColumns.push({
        header: getStoreGroupsCount(header),
        accessorKey: header,
        columnClassname:
          index < dynamicHeaders.length - 1
            ? dynamicHeaders.length < 6
              ? 'dynamicHeader150'
              : 'dynamicHeader'
            : 'dynamicHeaderLastItem',
        enableSorting: true,
        cell: isIngredientsPresence
          ? getPresenceDescription(header, modifierCategory, setPriceDeviationsData)
          : getPriceDescription(header, inactiveLabel, modifierCategory, setPriceDeviationsData),
        sortingFn: isIngredientsPresence ? 'sortModifier' : 'sortModifierByPrice',
      } as any);
    });

    return lColumns;
  }, [
    t,
    dynamicHeaders,
    getStoreGroupsCount,
    modifierCategory,
    setPriceDeviationsData,
    isIngredientsPresence,
    useDevelopTableLayout,
  ]);

  return (
    <div className={rowStyles.tablePage}>
      <div>
        <Box>
          <FZMenuModifierTable
            returnedRecordsText={(count) => t('menu.table.returnedRecord', { count })}
            data={menuModifiers}
            isLoading={isLoading}
            enableRowSelection
            columns={columns}
            selectedRows={selectedRows}
            onRowSelectionChange={onRowSelectionChange}
            changeHeaderSize={changeHeaderSize}
            isFranchiseeEditAndFreeze={isFranchiseeEditAndFreeze}
            hidePriceLockIcons={isIngredientsPresence}
            useDevelopTableLayout={useDevelopTableLayout}
          />
        </Box>
      </div>
    </div>
  );
};

export default FZStoreModifierItemTable;
